import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Sheet, SheetClose, SheetPortal } from "@/components/ui/sheet";

export interface RecordDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /** Se true, alza lo z-index sopra i modal z-50 (es. apertura dal dettaglio email) */
  elevated?: boolean;
}

/**
 * RecordDrawer
 *
 * Side panel (right) basato su Radix Dialog tramite Sheet.
 * - ESC e click sull'overlay chiudono (gestiti da Radix via onOpenChange).
 * - Overlay leggero (bg-black/40) cosi' la lista sottostante resta visibile.
 * - X sempre visibile, oltre a ESC e click sull'overlay: nessun drawer puo'
 *   imprigionare l'utente su una superficie lunga.
 * - Larghezza w-[520px] su desktop, w-full su mobile.
 * - I children gestiscono il proprio padding (body con p-0).
 */
export function RecordDrawer({
  open,
  onClose,
  title,
  children,
  elevated = false,
}: RecordDrawerProps) {
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      onClose();
    }
  };
  const zIndexClass = elevated ? "z-[60]" : "z-50";

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetPortal>
        <SheetPrimitive.Overlay
          className={cn(
            "fixed inset-0 bg-black/40",
            zIndexClass,
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <SheetPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            "fixed inset-y-0 right-0 flex h-dvh max-h-dvh flex-col",
            zIndexClass,
            "w-full sm:w-[520px]",
            "bg-background border-l border-border shadow-lg",
            "transition ease-in-out",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:duration-300 data-[state=open]:duration-500",
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
            "focus:outline-none"
          )}
        >
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border p-3 pl-4">
            {title ? (
              <SheetPrimitive.Title className="min-w-0 flex-1 text-base font-medium text-foreground">
                {title}
              </SheetPrimitive.Title>
            ) : (
              <SheetPrimitive.Title className="sr-only">Dettaglio</SheetPrimitive.Title>
            )}
            <SheetClose
              type="button"
              aria-label="Chiudi"
              onClick={onClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Chiudi</span>
            </SheetClose>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-0 pb-[calc(1rem+env(safe-area-inset-bottom))]">{children}</div>
        </SheetPrimitive.Content>
      </SheetPortal>
    </Sheet>
  );
}

export default RecordDrawer;
